import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Gift, Copy, Share2, Users, Check, Sparkles } from "lucide-react";

const ReferralCard = ({ token }) => {
  const [code, setCode] = useState(null);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const [codeRes, statsRes] = await Promise.all([
          axios.get(`${API}/referral/code`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/referral/stats`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setCode(codeRes.data.code);
        setStats(statsRes.data);
      } catch (e) {
        console.error("Referral fetch failed:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const referralLink = code
    ? `${window.location.origin}/?ref=${code}`
    : "";

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Referral code copied!");
    setTimeout(() => setCopied(false), 1500);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success("Referral link copied!");
  };

  const shareNative = async () => {
    const text = `Join me on StardomFans.com — the home of independent artists and their biggest fans. Use my code ${code} to get started: ${referralLink}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "StardomFans.com", text, url: referralLink });
      } else {
        navigator.clipboard.writeText(text);
        toast.success("Share message copied!");
      }
    } catch {}
  };

  if (loading) return <div className="card p-6"><div className="h-32 animate-pulse bg-[#1A2030] rounded" /></div>;
  if (!code) return null;

  return (
    <div className="card p-6 relative overflow-hidden" data-testid="referral-card">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#EC008C] via-[#FF6BC5] to-[#D14A85]" />
      <div className="absolute -top-12 -left-12 w-48 h-48 rounded-full opacity-10 blur-3xl bg-[#EC008C]" />

      <div className="relative z-10">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#EC008C]/15 border border-[#EC008C]/30 flex items-center justify-center">
              <Gift className="w-6 h-6 text-[#EC008C]" />
            </div>
            <div>
              <h3 className="font-['Unbounded'] text-xl font-bold text-white">Invite & Earn</h3>
              <p className="text-xs text-[#8A92A0]">Share your code. Earn 50 XP per signup.</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-[#8A92A0] uppercase tracking-wide">Total Referrals</p>
            <p className="text-2xl font-bold text-[#EC008C]" data-testid="referral-total">{stats?.total || 0}</p>
          </div>
        </div>

        {/* Code Display */}
        <div className="bg-[#050810] rounded-xl p-4 border border-[#EC008C]/30 mb-4">
          <p className="text-xs text-[#8A92A0] uppercase tracking-wide mb-1">Your Referral Code</p>
          <div className="flex items-center gap-3">
            <code className="font-mono text-2xl font-bold text-[#EC008C] tracking-wider flex-1" data-testid="referral-code">{code}</code>
            <button
              onClick={copyCode}
              className="px-4 py-2 rounded-lg bg-[#EC008C] text-[#000000] font-semibold hover:bg-[#FF3399] transition-colors flex items-center gap-2 text-sm"
              data-testid="copy-code-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <button
            onClick={copyLink}
            className="px-4 py-2.5 rounded-lg bg-[#1A2030] text-white hover:bg-[#2A3040] transition-colors flex items-center justify-center gap-2 text-sm font-medium border border-[#1A2030]"
            data-testid="copy-link-btn"
          >
            <Copy className="w-4 h-4" /> Copy Link
          </button>
          <button
            onClick={shareNative}
            className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#EC008C]/20 to-[#FF6BC5]/20 text-[#EC008C] hover:from-[#EC008C]/30 hover:to-[#FF6BC5]/30 transition-colors flex items-center justify-center gap-2 text-sm font-medium border border-[#EC008C]/30"
            data-testid="share-referral-btn"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>

        {/* Recent Referrals */}
        {stats?.referrals?.length > 0 && (
          <div>
            <p className="text-xs text-[#8A92A0] uppercase tracking-wide mb-2 flex items-center gap-1">
              <Users className="w-3 h-3" /> Recent Signups
            </p>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {stats.referrals.slice(0, 10).map((r) => (
                <div key={r.referral_id} className="flex items-center gap-3 bg-[#050810] rounded-lg p-2.5 border border-[#1A2030]" data-testid="referral-entry">
                  <div className="w-7 h-7 rounded-full bg-[#EC008C]/10 flex items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-[#EC008C]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{r.referred_name}</p>
                    <p className="text-[10px] text-[#4A5260]">{new Date(r.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="text-[#EC008C] text-xs font-bold">+50 XP</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {stats?.total === 0 && (
          <div className="text-center py-4 text-[#4A5260] text-sm">
            No referrals yet. Share your code to earn XP and unlock badges!
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralCard;
